import * as React from "react";
import "./EventHistoryOverlay.component.css";
import { historyEventService } from "../../../../../../modules/multiplayer/peer";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HistoryEventType } from "../../../../../../messages/scripting/ScriptMessages";
import { EventHistoryUtils } from "../EventHistoryList.util";
import { MenuButton } from "../../../../../components/menu-button/MenuButton.component";
import { ttCopyToClipboardButton } from "../../../../../common-strings.util";
import { useTranslation } from "react-i18next";
const maxHistoryItemsFull = 10;
const maxHistoryItemsReduced = 3;
const autoHideAfterSeconds = 15;
const ttPosChatControls = "top-right";
let interval;
export function EventHistoryOverlay({ onOpenSidePanelClicked }) {
    const [completeHistory, setCompleteHistory] = useState([]);
    const [history, setHistory] = useState([]);
    const [inputContent, setInputContent] = useState("");
    const [viewMode, setViewMode] = useState(EventHistoryUtils.EventHistoryOverlayViewMode.expanded);
    const [autoHide, setAutoHide] = useState(true);
    const inputRef = useRef(null);
    const { t } = useTranslation();
    useEffect(() => {
        const options = EventHistoryUtils.loadEventHistoryOptions();
        if (options.overlayViewMode) {
            setViewMode(options.overlayViewMode);
        }
        if (options.overlayAutoHide !== undefined) {
            setAutoHide(options.overlayAutoHide);
        }
        const onHistoryChanged = () => {
            setCompleteHistory([...historyEventService.history]);
        };
        historyEventService.historyChangedDelegate.add(onHistoryChanged);
        return () => {
            historyEventService.historyChangedDelegate.remove(onHistoryChanged);
        };
    }, []);
    useEffect(() => {
        setHistory(filterOutOldEvents(completeHistory));
    }, [completeHistory]);
    useEffect(() => {
        EventHistoryUtils.persistEventHistoryOptions({
            overlayViewMode: viewMode,
            overlayAutoHide: autoHide
        });
    }, [viewMode, autoHide]);
    useEffect(() => {
        if (autoHide) {
            if (!interval) {
                const reduceHistory = () => {
                    setHistory((oldHistory) => filterOutOldEvents(oldHistory));
                };
                interval = window.setInterval(reduceHistory, 1000);
                reduceHistory(); // Run once immediately
            }
        }
        else {
            setHistory(completeHistory);
            window.clearInterval(interval);
            interval = undefined;
        }
    }, [history, autoHide]);
    const filterOutOldEvents = (events) => {
        const now = new Date().getTime();
        const newHistory = events.filter((event) => {
            var _a;
            const diff = now - ((_a = event.timeReceived) !== null && _a !== void 0 ? _a : 0);
            return diff < 1000 * autoHideAfterSeconds;
        });
        return newHistory;
    };
    const sendMessage = () => {
        var _a;
        const message = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value;
        if (message) {
            EventHistoryUtils.sendChatMessage(message);
            if (inputRef.current) {
                inputRef.current.value = "";
            }
            setInputContent("");
        }
    };
    const getItemOpacity = (index) => {
        const step = 1 / (viewMode === EventHistoryUtils.EventHistoryOverlayViewMode.expanded ? maxHistoryItemsFull : maxHistoryItemsReduced);
        const opacity = 0.9 - (index * step) / 2;
        return opacity.toFixed(2);
    };
    const createEventHistoryItem = (event, index) => (React.createElement("div", { key: index, className: EventHistoryUtils.getItemClassNames(event.params.clientId, "overlay"), style: { opacity: getItemOpacity(index) } },
        React.createElement("div", { className: "event-history-overlay-item-author" }, EventHistoryUtils.getUsername(event)),
        React.createElement("div", { className: "event-history-overlay-item-content" },
            React.createElement("div", { className: "event-history-overlay-item-icon-container", "data-tooltip": EventHistoryUtils.getIconTooltip(event.params.type) },
                React.createElement(FontAwesomeIcon, { icon: ["fas", EventHistoryUtils.getIconName(event.params.type)] })),
            React.createElement("div", { className: "event-history-overlay-item-main-content" }, EventHistoryUtils.createValueElement(event.params)),
            EventHistoryUtils.isCopyAllowed(event.params) && (React.createElement("button", { className: "icon-button-small event-history-overlay-item-button", onClick: () => {
                    EventHistoryUtils.copyToClipboard(event.params);
                }, "data-tooltip": ttCopyToClipboardButton() },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "clone"] })))),
        React.createElement("div", { className: "event-history-overlay-item-fade-out" })));
    const getFilteredHistory = () => {
        switch (viewMode) {
            case EventHistoryUtils.EventHistoryOverlayViewMode.expanded:
                return [...history.slice(0 - maxHistoryItemsFull).reverse()];
            case EventHistoryUtils.EventHistoryOverlayViewMode.reduced:
                return [
                    ...history
                        .filter((event) => event.params.type === HistoryEventType.chatMessage)
                        .slice(0 - maxHistoryItemsReduced)
                        .reverse()
                ];
            case EventHistoryUtils.EventHistoryOverlayViewMode.hideAll:
                return [];
        }
    };
    const createEventHistoryList = () => getFilteredHistory().map((event, index) => createEventHistoryItem(event, index));
    const createMenuOptions = () => [
        {
            icon: "calendar-plus",
            text: t("chatview:overlay-display-option-show-all", "Show all recent events"),
            checked: viewMode === EventHistoryUtils.EventHistoryOverlayViewMode.expanded,
            onClick: () => {
                setViewMode(EventHistoryUtils.EventHistoryOverlayViewMode.expanded);
            }
        },
        {
            icon: "calendar-minus",
            text: t("chatview:overlay-display-option-show-latest", "Show only latest chat messages"),
            checked: viewMode === EventHistoryUtils.EventHistoryOverlayViewMode.reduced,
            onClick: () => {
                setViewMode(EventHistoryUtils.EventHistoryOverlayViewMode.reduced);
            }
        },
        {
            icon: "times",
            text: t("chatview:overlay-display-option-hide-all", "Hide all events"),
            checked: viewMode === EventHistoryUtils.EventHistoryOverlayViewMode.hideAll,
            onClick: () => {
                setViewMode(EventHistoryUtils.EventHistoryOverlayViewMode.hideAll);
            }
        },
        {
            divider: true
        },
        {
            icon: "stopwatch",
            text: t("chatview:overlay-display-option-autohide", "Auto hide older events"),
            checked: autoHide,
            onClick: () => {
                setAutoHide((prev) => !prev);
            }
        }
    ];
    return (React.createElement("div", { className: "event-history-overlay-container" },
        React.createElement("div", { className: "event-history-overlay" }, createEventHistoryList()),
        React.createElement("div", { className: "event-history-overlay-chat-controls-container" },
            React.createElement(MenuButton, { icon: "ellipsis-v", menuTitle: t("chatview:overlay-button-display-options-menu-title", "Chat display options"), closeOnSelect: true, menuPlacement: "top-right", size: "medium", options: createMenuOptions(), tooltip: t("chatview:overlay-button-display-options-tooltip", "Chat display options"), tooltipPosition: ttPosChatControls }),
            React.createElement("div", { className: "event-history-overlay-input-container" },
                React.createElement("input", { type: "text", className: "event-history-overlay-input", placeholder: EventHistoryUtils.placeholderTypeAMessage(), ref: inputRef, onChange: (e) => {
                        setInputContent(e.target.value);
                    }, onKeyUp: (e) => {
                        e.code === "Enter" && sendMessage();
                    } }),
                React.createElement("button", { className: "icon-button", onClick: sendMessage, disabled: !inputContent, "data-tooltip": EventHistoryUtils.ttSendMessageButton(), "data-tooltip-disabled": EventHistoryUtils.ttSendMessageButtonDisabled(), "data-tooltip-position": ttPosChatControls },
                    React.createElement(FontAwesomeIcon, { icon: ["fas", "paper-plane"] }))),
            React.createElement("button", { className: "icon-button", onClick: onOpenSidePanelClicked, "data-tooltip": t("chatview:overlay-button-expand-chatview", "Expand chat in left sidebar"), "data-tooltip-position": ttPosChatControls },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "angle-double-right"] })))));
}
